import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useDataTableStore } from '#stores/dataTable.store';
import {
  type DataTableDimensionsType,
  type DataTableKpiDisplayNameType,
  type DataTableViewType
} from '@carrefour-gcs/shared';
import { ref, watch, type Ref } from 'vue';

interface UseDataTableConfigurationParams {
  dataTableView: Ref<DataTableViewType>;
  availableKpis: Ref<{ name: DataTableKpiDisplayNameType }[]> | Ref<undefined>;
  availableDimensions: Ref<{ name: DataTableDimensionsType }[]> | Ref<undefined>;
  updateSelectedKpis(kpis: DataTableKpiDisplayNameType[]): void;
  updateSelectedDimensions(dimensions: DataTableDimensionsType[]): void;
  resetExport(): void;
  resetSelectedDimensions(): void;
  resetSelectedKpis(): void;
}

export function useDataTableUserConfiguration({
  dataTableView,
  availableKpis,
  availableDimensions,
  updateSelectedKpis,
  updateSelectedDimensions,
  resetExport,
  resetSelectedDimensions,
  resetSelectedKpis
}: UseDataTableConfigurationParams) {
  const isConfigurationPopInOpen = ref<boolean>(false);
  const isExportPopInOpen = ref<boolean>(false);
  const dataTableStore = useDataTableStore();
  const {
    taxonomy: { taxonomy }
  } = useKpisPerimeter();

  function openConfigurationPopIn() {
    isConfigurationPopInOpen.value = true;
  }

  function closeConfigurationPopIn() {
    isConfigurationPopInOpen.value = false;
  }

  function openExportPopIn() {
    isExportPopInOpen.value = true;
  }

  function closeExportPopIn() {
    isExportPopInOpen.value = false;
    resetExport();
  }

  const configurationKpisColumnsOrder = ref<DataTableKpiDisplayNameType[]>(getDefaultKpisColumnsOrder());
  const configurationDimensionsOrder = ref<DataTableDimensionsType[]>(getDefaultDimensionsOrder());

  watch(
    availableKpis,
    (availableKpis) => {
      if (!availableKpis?.length) {
        return;
      }
      const savedKpisColumnsOrder = dataTableStore.getKpisColumnsOrder(taxonomy.value, dataTableView.value);
      if (savedKpisColumnsOrder) {
        // Remove non-existing saved kpis
        const savedKpisColumnsOrderFiltered = savedKpisColumnsOrder.filter((kpi) =>
          availableKpis.find((savedSelectedKpi) => savedSelectedKpi.name === kpi)
        );

        updateKpisColumnsOrder(savedKpisColumnsOrderFiltered);
      } else {
        updateKpisColumnsOrder(getDefaultKpisColumnsOrder());
      }
    },
    {
      immediate: true
    }
  );

  watch(
    availableDimensions,
    (availableDimensions) => {
      if (!availableDimensions?.length) {
        return;
      }
      const savedDimensionsOrder = dataTableStore.getDimensionsOrder(taxonomy.value, dataTableView.value);
      if (savedDimensionsOrder) {
        // Remove non-existing saved dimensions
        const savedDimensionsOrderFiltered = savedDimensionsOrder.filter((dimension) =>
          availableDimensions.find((availableDimension) => availableDimension.name === dimension)
        );

        updateDimensionsOrder(savedDimensionsOrderFiltered);
      } else {
        updateDimensionsOrder(getDefaultDimensionsOrder());
      }
    },
    {
      immediate: true
    }
  );

  function getDefaultKpisColumnsOrder() {
    return availableKpis.value?.length ? availableKpis.value.map((kpi) => kpi.name) : [];
  }

  function getDefaultDimensionsOrder() {
    return availableDimensions.value?.length ? availableDimensions.value.map((dimension) => dimension.name) : [];
  }

  function updateKpisColumnsOrder(kpisColumnsOrder: DataTableKpiDisplayNameType[]) {
    configurationKpisColumnsOrder.value = kpisColumnsOrder;
    dataTableStore.updateKpisColumnsOrder(taxonomy.value, dataTableView.value, kpisColumnsOrder);
  }

  function updateDimensionsOrder(dimensionsOrder: DataTableDimensionsType[]) {
    configurationDimensionsOrder.value = dimensionsOrder;
    dataTableStore.updateDimensionsOrder(taxonomy.value, dataTableView.value, dimensionsOrder);
  }

  function resetKpisColumnsOrder() {
    dataTableStore.resetKpisColumnsOrder(taxonomy.value, dataTableView.value);
    updateKpisColumnsOrder(getDefaultKpisColumnsOrder());
  }

  function resetDimensionsOrder() {
    dataTableStore.resetDimensionsOrder(taxonomy.value, dataTableView.value);
    updateDimensionsOrder(getDefaultDimensionsOrder());
  }

  function updateConfiguration(
    formValues: {
      selectedKpis: DataTableKpiDisplayNameType[];
      selectedDimensions: DataTableDimensionsType[];
      kpisColumnsOrder: DataTableKpiDisplayNameType[];
      dimensionsOrder: DataTableDimensionsType[];
    },
    withExport = false
  ) {
    // updateKpisColumnsOrder and updateDimensionsOrder must appear first.
    // Else, the configurationKpisColumnsOrder will not be up to date on fetchDataTableColumns dependencies
    // triggered by updateSelectedKpis and updateSelectedDimensions
    updateKpisColumnsOrder(formValues.kpisColumnsOrder);
    updateDimensionsOrder(formValues.dimensionsOrder);

    updateSelectedKpis(formValues.selectedKpis);
    updateSelectedDimensions(formValues.selectedDimensions);
    closeConfigurationPopIn();

    if (withExport) {
      openExportPopIn();
    }
  }

  function resetConfiguration() {
    resetSelectedDimensions();
    resetSelectedKpis();
    resetKpisColumnsOrder();
    resetDimensionsOrder();
  }

  return {
    isConfigurationPopInOpen,
    isExportPopInOpen,
    openConfigurationPopIn,
    openExportPopIn,
    closeConfigurationPopIn,
    closeExportPopIn,
    configurationKpisColumnsOrder,
    configurationDimensionsOrder,
    updateConfiguration,
    resetConfiguration
  };
}
